import React, { useMemo, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box, Grid, Stack, Snackbar, Alert, CircularProgress, Dialog, DialogContent,
} from '@mui/material';
import ProjectPopup from '../Project/ProjectPopup';
import OpportunityDetailHeader from './components/cards/OpportunityDetailHeader';
import ContactInfoCard from './components/cards/ContactInfoCard';
import OpportunityInfoCard from './components/cards/OpportunityInfoCard';
import JobResultCard from './components/cards/JobResultCard';
import GoogleDriveCard from './components/cards/GoogleDriveCard';
import StatusActionsCard from './components/cards/StatusActionsCard';
import NoteCard from './components/cards/NoteCard';
import StatusChangeDialog from './components/dialogs/StatusChangeDialog';
import FinishJobDialog from './components/dialogs/FinishJobDialog';
import JobResultEditDialog from './components/dialogs/JobResultEditDialog';
import TenderDocumentsPanel from './components/lists/TenderDocumentsPanel';
import SaleDocumentsCard from './components/lists/SaleDocumentsCard';
import StatusHistoryList from './components/lists/StatusHistoryList';
import OwnerHistoryList from './components/lists/OwnerHistoryList';
import NoteHistoryList from './components/lists/NoteHistoryList';
import CheckinHistoryList from './components/lists/CheckinHistoryList';
import { useOpportunityDetail } from './hooks/useOpportunityDetail';
import { useStatusChange } from './hooks/useStatusChange';
import { useTenderDocuments } from './hooks/useTenderDocuments';
import { useFinishJob } from './hooks/useFinishJob';
import { useJobResultEdit } from './hooks/useJobResultEdit';
import { useOpportunityDocuments } from './hooks/useOpportunityDocuments';
import { useCreateProject } from './hooks/useCreateProject';
import { useLinkedProject } from './hooks/useLinkedProject';
import { useCreateSaleDocument } from './hooks/useCreateSaleDocument';
import CreateProjectModal from './components/dialogs/CreateProjectModal';
import CreateSaleDocumentModal from './components/dialogs/CreateSaleDocumentModal';
import { formatEmployeeThaiName } from '../../utils/functions/employeeName';

// propId/onClose: ให้หน้า Project เปิดเนื้อหานี้เป็น modal ได้ (ดูโอกาสทางธุรกิจต้นทาง
// ของโครงการที่ถูกสร้างจาก opportunity) — ไม่ใส่ propId ก็ทำงานเป็นหน้าเต็มตามปกติ
// (อ่าน id จาก route param แทน) ปุ่มย้อนกลับก็สลับไปเรียก onClose() แทน navigate
export default function OpportunityDetail({ propId, onClose } = {}) {
  const params = useParams();
  const id = propId || params.id;
  const history = useHistory();

  const {
    opportunity, loading, snack, showSnack, closeSnack, refetch, applyLocalPatch,
  } = useOpportunityDetail(id);

  // ใช้ก้อนพนักงานเดียวกับที่ useOpportunityDashboard (ใน OpportunityInfoCard) fetch ไว้แล้ว
  // (dispatch(employeeAll) ครั้งเดียวพอ ไม่ fetch ซ้ำที่นี่) — resolve ชื่อสำหรับประวัติเปลี่ยน
  // ผู้รับผิดชอบ เหมือน ownerNameById/creatorNameByUserId ใน OpportunityList.js
  const employee = useSelector((state) => state.employee);
  const ownerNameById = useMemo(() => {
    const map = {};
    (employee?.rows || []).forEach((e) => {
      map[e._id || e.id] = formatEmployeeThaiName(e);
    });
    return map;
  }, [employee?.rows]);
  const creatorNameByUserId = useMemo(() => {
    const map = {};
    (employee?.rows || []).forEach((e) => {
      if (e.uid) map[e.uid] = formatEmployeeThaiName(e);
    });
    return map;
  }, [employee?.rows]);

  const statusChange = useStatusChange({ opportunity, onChanged: refetch, showSnack });
  const tenderDocs = useTenderDocuments({ opportunity, onChanged: refetch, showSnack });
  const finishJob = useFinishJob({ onChanged: refetch, showSnack });
  const jobResultEdit = useJobResultEdit({ opportunity, onChanged: refetch, showSnack });
  const saleDocuments = useOpportunityDocuments(id);
  const createProject = useCreateProject({ opportunity: opportunity || {}, showSnack });
  const { project: linkedProject } = useLinkedProject(opportunity?.project_id);
  const [projectPopupOpen, setProjectPopupOpen] = useState(false);
  const createDocument = useCreateSaleDocument({
    opportunity: opportunity || {}, documents: saleDocuments.rows, showSnack, onCreated: saleDocuments.refetch,
  });

  if (loading || !opportunity) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <OpportunityDetailHeader
        opportunity={opportunity}
        onBack={() => (onClose ? onClose() : history.push('/marketing/opportunities'))}
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Stack spacing={3}>
            <OpportunityInfoCard
              opportunity={opportunity}
              onChanged={refetch}
              onLocalUpdate={applyLocalPatch}
              showSnack={showSnack}
            />

            <JobResultCard opportunity={opportunity} onEdit={jobResultEdit.openDialog} />

            <GoogleDriveCard
              opportunity={opportunity}
              onChanged={refetch}
              onLocalUpdate={applyLocalPatch}
              showSnack={showSnack}
            />

            <SaleDocumentsCard
              documents={saleDocuments.rows}
              loading={saleDocuments.loading}
              onCreateDocument={createDocument.openDialog}
            />

            <TenderDocumentsPanel
              documents={opportunity.marketing_tender_documents}
              docType={tenderDocs.docType}
              onDocTypeChange={tenderDocs.setDocType}
              docFiles={tenderDocs.docFiles}
              onFilesChange={tenderDocs.setDocFiles}
              uploading={tenderDocs.uploading}
              onUpload={tenderDocs.upload}
              onDelete={tenderDocs.remove}
            />

            <ContactInfoCard contactId={opportunity.contact_id} />
          </Stack>
        </Grid>

        <Grid item xs={12} md={5}>
          <Stack spacing={3}>
            <NoteCard
              opportunity={opportunity}
              onChanged={refetch}
              onLocalUpdate={applyLocalPatch}
              showSnack={showSnack}
            />
            <StatusActionsCard
              currentStatus={opportunity.status}
              isTender={opportunity.is_tender}
              onSelect={statusChange.openStatusDialog}
              onFinishJob={() => finishJob.openDialog(opportunity)}
              onCreateProject={createProject.openDialog}
              linkedProject={linkedProject}
              onOpenProject={() => setProjectPopupOpen(true)}
            />
            <StatusHistoryList history={opportunity.marketing_status_history} />
            <OwnerHistoryList
              history={opportunity.marketing_owner_history}
              ownerNameById={ownerNameById}
              creatorNameByUserId={creatorNameByUserId}
            />
            <NoteHistoryList
              history={opportunity.marketing_note_history}
              creatorNameByUserId={creatorNameByUserId}
            />
            <CheckinHistoryList
              history={opportunity.marketing_checkins}
              ownerNameById={ownerNameById}
            />
          </Stack>
        </Grid>
      </Grid>

      <StatusChangeDialog
        statusDialog={statusChange.statusDialog}
        saving={statusChange.saving}
        onClose={statusChange.closeStatusDialog}
        onNoteChange={statusChange.setNote}
        onConfirm={statusChange.handleStatusChange}
      />

      <FinishJobDialog
        open={finishJob.open}
        result={finishJob.result}
        onResultChange={finishJob.setResult}
        poNumber={finishJob.poNumber}
        onPoNumberChange={finishJob.setPoNumber}
        value={finishJob.value}
        onValueChange={finishJob.setValue}
        note={finishJob.note}
        onNoteChange={finishJob.setNote}
        docs={finishJob.docs}
        onAddDoc={finishJob.addDocRow}
        onRemoveDoc={finishJob.removeDocRow}
        onDocNameChange={finishJob.setDocName}
        onDocFileChange={finishJob.setDocFile}
        saving={finishJob.saving}
        onClose={finishJob.closeDialog}
        onConfirm={finishJob.confirm}
      />

      <JobResultEditDialog
        open={jobResultEdit.open}
        value={jobResultEdit.value}
        onValueChange={jobResultEdit.setValue}
        poNumber={jobResultEdit.poNumber}
        onPoNumberChange={jobResultEdit.setPoNumber}
        saving={jobResultEdit.saving}
        onClose={jobResultEdit.closeDialog}
        onConfirm={jobResultEdit.save}
      />

      <CreateProjectModal
        open={createProject.open}
        onClose={createProject.closeDialog}
        saving={createProject.saving}
        onSubmit={createProject.submit}
        form={createProject.form}
        date={createProject.date}
        setDate={createProject.setDate}
        dateEnd={createProject.dateEnd}
        setDateEnd={createProject.setDateEnd}
        includeVAT={createProject.includeVAT}
        setIncludeVAT={createProject.setIncludeVAT}
        cost={createProject.cost}
        setCost={createProject.setCost}
        extraGpsSites={createProject.extraGpsSites}
        setExtraGpsSites={createProject.setExtraGpsSites}
        allEmployees={createProject.allEmployees}
        contacts={createProject.contacts}
        selectedClient={createProject.selectedClient}
        setSelectedClient={createProject.setSelectedClient}
        setValue={createProject.setValue}
      />

      <CreateSaleDocumentModal
        open={createDocument.open}
        docType={createDocument.docType}
        onClose={createDocument.closeDialog}
        doc={createDocument}
      />

      {/* กด card สรุปโครงการใน StatusActionsCard → เปิด P111 project popup ตัวเดียวกับที่
      ใช้ทั่วระบบ (mirror OpportunitiesTable.js) */}
      <Dialog
        open={projectPopupOpen}
        onClose={() => setProjectPopupOpen(false)}
        maxWidth="xl"
        fullWidth
        sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
        PaperProps={{
          sx: { mt: '40px', maxHeight: 'calc(100vh - 56px)', minWidth: '50vw' },
        }}
        hideBackdrop
      >
        <DialogContent sx={{ p: 0 }}>
          {linkedProject && (
            <ProjectPopup
              propId={linkedProject._id}
              onClose={() => setProjectPopupOpen(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snack.open} autoHideDuration={3000}
        onClose={closeSnack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snack.severity} onClose={closeSnack}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
